import { defineStore, storeToRefs } from 'pinia'
import { onScopeDispose, ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { ImageUpscale, Pickaxe, TicketPlus } from 'lucide-vue-next'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatInTimeZone } from 'date-fns-tz'
import { useCompatibilityStore } from './compatibilityStore'
import { useImageStore } from '@/stores/imageStore'
import { useAuthStore } from '@/stores/authStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { useFullscreenStore } from '@/stores/fullscreenStore'
import type { IImage } from '@/types'
import { ENotificationType, GenType, RenderType } from '@/types'
import Tools from '@/utils/tools'
import type { Generation } from '@/stores/GeneratorStore'
import { GenerationImageType, GenerationState, GenerationType, useGeneratorStore } from '@/stores/GeneratorStore'

export const useGeneratorClientStore = defineStore('generator client  store', () => {
	const { t } = useI18n()

	const route = useRoute()
	const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
	const genStore = useGeneratorStore()

	const { mobileDevice } = storeToRefs(useCompatibilityStore())

	const { tweakImages } = storeToRefs(genStore)

	function createImage(gen: Generation): IImage {
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
		const parsedDate = new Date(Date.now())
		const created_at = formatInTimeZone(parsedDate, timeZone, t('date_format')).split(' ')[0]

		return {
			id: gen?.image_id ?? Tools.random(-1, -9999999999),
			uuid: gen.image_uuid == null ? `tmp-${uuidv4()}` : gen.image_uuid,
			image_src: gen.image,
			is_favorite: false,
			is_private: gen.private,
			image_type: gen.imageType,
			tags: gen.tags ?? '',
			upscaled: false,
			is_updating: true,
			is_user_gen: true,
			skeleton: false,
			style_id: 1,
			format: gen.format,
			gen_type: GenerationType.Generation,
			render: RenderType.Hentai,
			gen_data: {
				prompt: gen.prompt,
				neg_prompt: gen.neg_prompt,
				seed: gen.seed,
				upscale: gen.upscale,
				watermark: gen.watermark,
				private: gen.private,
				quality: gen.quality,
				format: gen.format,
				style_id: gen.style_id,
				creativity: gen.creativity,
				tags: gen.tags,
				created_at: gen.created_at ?? created_at,
				user_id: gen.user_id,
				username: gen.username,
				modifiers: gen.modifiers,
				less_nsfw: gen.less_nsfw,
			},
		}
	}

	const sseGen = ref<Generation[]>([])
	const mapping = ref<Record<number, IImage>>({})
	const generations = ref<Generation[]>([])
	const done = ref<number[]>([])
	const tweakProgress = ref<IImage | null>()

	let interval: ReturnType<typeof setInterval>

	async function getGenerations(ids: number[]): Promise<Generation[]> {
		const proms = ids.map(id => genStore.getItemDb(id))

		const results = await Promise.all(proms)
		const filteredGen = results.filter((gen): gen is Generation => gen !== undefined)

		return filteredGen
	}

	function updateImage(gen: Generation, isUpdating: boolean, isInPaint: boolean): void {
		mapping.value[gen.id].is_updating = isUpdating
		mapping.value[gen.id].image_type = gen.imageType
		mapping.value[gen.id].state = gen.state
		mapping.value[gen.id].is_private = gen.private ?? false
		mapping.value[gen.id].format = gen.format
		mapping.value[gen.id].upscaled = gen.upscale
		mapping.value[gen.id].style_id = gen.style_id
		mapping.value[gen.id].image_src = gen.image
		mapping.value[gen.id].gen_type = gen.genType
		mapping.value[gen.id].gen_data.seed = gen.seed
		mapping.value[gen.id].gen_data.format = gen.format
		mapping.value[gen.id].gen_data.private = gen.private
		mapping.value[gen.id].gen_data.prompt = gen.prompt
		mapping.value[gen.id].gen_data.neg_prompt = gen.neg_prompt
		mapping.value[gen.id].gen_data.quality = gen.quality
		mapping.value[gen.id].gen_data.watermark = gen.watermark
		mapping.value[gen.id].gen_data.creativity = gen.creativity
		mapping.value[gen.id].gen_data.upscale = gen.upscale
		mapping.value[gen.id].gen_data.modifiers = gen.modifiers
		mapping.value[gen.id].gen_data.less_nsfw = gen.less_nsfw
		mapping.value[gen.id].gen_data.style_id = gen.style_id
		mapping.value[gen.id].gen_data.source_image_id = gen.source_image_id

		if (isInPaint && mapping.value[gen.id] && gen.imageType >= GenerationImageType.Base64) {
			const existingImageIndex = tweakImages.value.findIndex(({ id }) => id === mapping.value[gen.id].id)

			if (existingImageIndex <= -1) {
				genStore.addTweakData(mapping.value[gen.id])
			}
		}

		if (gen.image_id !== null) {
			mapping.value[gen.id].id = gen.image_id
		}
		if (gen.image_uuid) {
			mapping.value[gen.id].uuid = gen.image_uuid
		}
		if (gen.tags != null) {
			mapping.value[gen.id].tags = gen.tags
			mapping.value[gen.id].gen_data.tags = gen.tags
		}
	}

	function run() {
		interval = setInterval(async () => {
			const data: string | null = localStorage.getItem('generations')
			if (data) {
				const ids = JSON.parse(data)

				sseGen.value = await getGenerations(ids)

				for (let i = 0; i < sseGen.value.length; i++) {
					const gen = sseGen.value[i]

					const isDone = done.value.includes(gen.id)

					const index = generations.value.findIndex(({ id }) => id === gen.id)
					if (index >= 0) {
						generations.value[index] = gen
					}
					else {
						generations.value.unshift(gen)
					}

					const isInPaint = gen.genType === GenerationType.StyleConvert || gen.genType === GenerationType.InPaint
					const isGeneration = gen.genType === GenerationType.Generation || isInPaint

					if (gen.created_at) {
						const parsedDate = new Date(gen.created_at)
						gen.created_at = formatInTimeZone(parsedDate, timeZone, t('date_format')).split(' ')[0]
					}

					if (!isDone && isGeneration) {
						if (!mapping.value[gen.id]) {
							if (gen.state !== GenerationState.Done || isInPaint) {
								const image = createImage(gen)
								mapping.value[gen.id] = image

								useImageStore().addImage(image, isInPaint ? 'creations' : 'generator')

								const { imageBucket } = storeToRefs(useImageStore())

								if (imageBucket.value.generator.index >= 0) {
									imageBucket.value.generator.index++
								}

								if (isInPaint && imageBucket.value.creations.index >= 0) {
									imageBucket.value.creations.index++
								}
							}
						}
						else {
							updateImage(gen, true, isInPaint)
						}
					}

					// only upscale
					if (gen.genType === GenerationType.Upscale && gen.state !== GenerationState.Done) {
						useImageStore().updateAllTargetImage(gen.image_uuid as string, {
							image_type: gen.imageType,
							image_src: gen.image,
							is_updating: true,
							state: gen.state,
						})
					}

					if (gen.state === GenerationState.Fail) {
						if (gen.genType === GenerationType.Upscale) {
							useImageStore().updateAllTargetImage(gen.image_uuid as string, {
								is_updating: false,
							})
						}
						if (!isDone) {
							done.value.push(gen.id)
							if (gen.created_at.includes('T')) {
								const parsedDate = new Date(gen.created_at)
								gen.created_at = formatInTimeZone(parsedDate, timeZone, t('date_format')).split(' ')[0]
							}
							if (isGeneration) {
								useImageStore().deleteAllTargetImage(mapping.value[gen.id].uuid)
								setTimeout(() => {
									delete mapping.value[gen.id]
								}, 5000)
							}
							if (gen.paid_with_daily) {
								useAuthStore().user.daily_token += gen.cost
							}
							else if (!gen.slow_mode) {
								useAuthStore().user.generation_token += gen.cost
							}
							useNotificationStore().push(ENotificationType.AppError, {
								key: `notification.${gen.error}`,
								delay: 4000,
							}, true)
							if (!gen.slow_mode) {
								useNotificationStore().push(ENotificationType.App, {
									key: `notification.ticket_refunded`,
									icon: TicketPlus,
									iconColor: 'text-emerald-400',
									delay: 4000,
									data: {
										amount: gen.cost,
									},
								}, true)
							}
						}
					}

					if (gen.state === GenerationState.Done) {
						if (!isDone) {
							if (isGeneration) {
								if (mapping.value[gen.id]) {
									updateImage(gen, false, isInPaint)
								}

								if (route.name !== 'Generator' && !mobileDevice.value) {
									useNotificationStore().push(ENotificationType.App, {
										key: `notification.generation_done`,
										icon: Pickaxe,
										iconColor: 'text-orange-400',
										action: () => {
											const currentPageImage = useImageStore().getImage(gen.image_uuid ?? '')
											if (currentPageImage) {
												useFullscreenStore().routeTo(currentPageImage as IImage)
											}
											else {
												window.open(`/image/${gen.image_uuid}`, '_blank')
											}
										},
										delay: 4000,
									}, true)
								}

								useImageStore().imageBucket.generator.offset++
								useImageStore().refreshRequired = true
							}
							else if (gen.genType === GenerationType.Upscale) {
								useImageStore().updateAllTargetImage(gen.image_uuid as string, {
									image_type: gen.imageType,
									image_src: gen.image,
									is_updating: false,
									upscaled: true,
									state: gen.state,
									gen_data: {
										upscale: true,
									},
								})
								if (!mobileDevice.value) {
									useNotificationStore().push(ENotificationType.App, {
										key: `notification.upscale_done`,
										icon: ImageUpscale,
										iconColor: 'text-orange-400',
										action: () => {
											const currentPageImage = useImageStore().getImage(gen.image_uuid ?? '')
											if (currentPageImage) {
												useFullscreenStore().routeTo(currentPageImage as IImage)
											}
											else {
												window.open(`/image/${gen.image_uuid}`, '_blank')
											}
										},
										delay: 4000,
									}, true)
								}
							}

							done.value.push(gen.id)
							delete mapping.value[gen.id]
						}
					}
					if (mapping.value[gen.id] && isInPaint) {
						tweakProgress.value = mapping.value[gen.id]
					}
				}
			}
		}, 400)
	}

	onScopeDispose(() => {
		clearInterval(interval)
		run()
	})

	run()

	function deleteTask(genId: number) {
		generations.value = generations.value.filter(({ id }) => id !== genId)
	}
	return {
		deleteTask,
		sseGen,
		generations,
		tweakProgress,
	}
})
